import React, { useRef, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/FirebaseAuthContext';
import { Link } from 'react-router-dom';

const notifications = [
  {
    icon: (
      <svg width="24" height="24" fill="none" stroke="#3B82F6" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="3" stroke="#3B82F6" strokeWidth="2" fill="#F3F8FF"/><path d="M8 8h8v8H8z" fill="#3B82F6"/></svg>
    ),
    message: 'You started reading "The Great Gatsby"',
    time: '2 hours ago',
  },
  {
    icon: (
      <svg width="24" height="24" fill="none" stroke="#22C55E" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#22C55E" strokeWidth="2" fill="#F3FCF7"/><path d="M8 12l2 2 4-4" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
    ),
    message: 'You completed "To Kill a Mockingbird"',
    time: '2 hours ago',
  },
];

const HeaderIconsWithDropdowns: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className = '', style }) => {
  const { user, signOut } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) setProfileOpen(false);
    }
    if (notifOpen || profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen, profileOpen]);

  return (
    <div className={`flex items-center ${className}`} style={style}>
      {/* Notification Icon */}
      <div className="relative mr-6">
        <button
          className="focus:outline-none w-8 h-8 flex items-center justify-center group"
          onClick={() => setNotifOpen(v => !v)}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-8 h-8 transition duration-150 text-[#333] group-hover:text-[#FF6600] group-active:text-[#FF6600]"
          >
            <g clipPath="url(#clip0_2517_2079)">
              <path opacity="0.3" d="M16.0003 8.66666C12.6803 8.66666 10.667 11.36 10.667 14.6667V22.6667H21.3337V14.6667C21.3337 11.36 19.3203 8.66666 16.0003 8.66666Z" fill="currentColor"/>
              <path d="M15.9997 29.3333C17.4663 29.3333 18.6663 28.1333 18.6663 26.6667H13.333C13.333 28.1333 14.533 29.3333 15.9997 29.3333ZM23.9997 21.3333V14.6667C23.9997 10.5733 21.8263 7.14668 17.9997 6.24001V5.33334C17.9997 4.22668 17.1063 3.33334 15.9997 3.33334C14.893 3.33334 13.9997 4.22668 13.9997 5.33334V6.24001C10.1863 7.14668 7.99967 10.56 7.99967 14.6667V21.3333L5.33301 24V25.3333H26.6663V24L23.9997 21.3333ZM21.333 22.6667H10.6663V14.6667C10.6663 11.36 12.6797 8.66668 15.9997 8.66668C19.3197 8.66668 21.333 11.36 21.333 14.6667V22.6667Z" fill="currentColor"/>
            </g>
            <defs>
              <clipPath id="clip0_2517_2079">
                <rect width="32" height="32" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </button>
        {notifOpen && (
          <div ref={notifRef} className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl z-50 p-0 border border-gray-100">
            <div className="flex items-center gap-2 px-6 pt-5 pb-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#E6FAF3]">
                <svg width="16" height="16" fill="none" viewBox="0 0 16 16"><path d="M3 8.5L6.5 12L13 5.5" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <span className="text-sm font-semibold text-gray-900">Notifications</span>
            </div>
            <div className="px-4 pb-4">
              {notifications.map((notif, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-[#F8FAFC] rounded-xl px-4 py-3 mb-3 last:mb-0">
                  <span>{notif.icon}</span>
                  <div>
                    <div className="text-sm text-gray-900 font-medium">{notif.message}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{notif.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Profile Dropdown */}
      <div className="relative">
        <button
          className="flex items-center justify-center w-8 h-8 focus:outline-none group"
          onClick={() => setProfileOpen(v => !v)}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-8 h-8 transition duration-150 text-[#333] group-hover:text-[#FF6600] group-active:text-[#FF6600]"
          >
            <g clipPath="url(#clip0_2331_1159)">
              <path
                opacity="0.3"
                d="M15.9997 5.33334C10.1063 5.33334 5.33301 10.1067 5.33301 16C5.33301 18.6 6.26634 20.9733 7.81301 22.8267C10.0663 21.0667 12.9063 20 15.9997 20C19.093 20 21.933 21.0667 24.1863 22.8267C25.733 20.9733 26.6663 18.6 26.6663 16C26.6663 10.1067 21.893 5.33334 15.9997 5.33334ZM15.9997 17.3333C13.4263 17.3333 11.333 15.24 11.333 12.6667C11.333 10.0933 13.4263 8 15.9997 8C18.573 8 20.6663 10.0933 20.6663 12.6667C20.6663 15.24 18.573 17.3333 15.9997 17.3333Z"
                fill="currentColor"
              />
              <path
                d="M15.9993 2.66667C8.63935 2.66667 2.66602 8.64 2.66602 16C2.66602 23.36 8.63935 29.3333 15.9993 29.3333C23.3593 29.3333 29.3327 23.36 29.3327 16C29.3327 8.64 23.3593 2.66667 15.9993 2.66667ZM15.9993 26.6667C13.6793 26.6667 11.546 25.92 9.79935 24.6667C11.546 23.4133 13.6793 22.6667 15.9993 22.6667C18.3193 22.6667 20.4527 23.4133 22.1993 24.6667C20.4527 25.92 18.3193 26.6667 15.9993 26.6667ZM24.186 22.8267C21.9327 21.0667 19.0927 20 15.9993 20C12.906 20 10.066 21.0667 7.81268 22.8267C6.26602 20.9733 5.33268 18.6 5.33268 16C5.33268 10.1067 10.106 5.33334 15.9993 5.33334C21.8927 5.33334 26.666 10.1067 26.666 16C26.666 18.6 25.7327 20.9733 24.186 22.8267Z"
                fill="currentColor"
              />
              <path
                d="M15.9997 7.90667C13.4263 7.90667 11.333 10 11.333 12.5733C11.333 15.1467 13.4263 17.24 15.9997 17.24C18.573 17.24 20.6663 15.1467 20.6663 12.5733C20.6663 10 18.573 7.90667 15.9997 7.90667ZM15.9997 14.5733C14.893 14.5733 13.9997 13.68 13.9997 12.5733C13.9997 11.4667 14.893 10.5733 15.9997 10.5733C17.1063 10.5733 17.9997 11.4667 17.9997 12.5733C17.9997 13.68 17.1063 14.5733 15.9997 14.5733Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_2331_1159">
                <rect width="32" height="32" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </button>
        {profileOpen && (
          <div ref={profileRef} className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
            <div className="px-4 py-3 border-b border-gray-100">
              <span className="block text-sm text-gray-900">{user?.email}</span>
            </div>
            <Link
              to="/settings"
              className="flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setProfileOpen(false)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3" stroke="currentColor" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h.09A1.65 1.65 0 008 3.09V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h.09a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.09a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" />
              </svg>
              Settings
            </Link>
            <button
              onClick={signOut}
              className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-gray-50 rounded-b-lg"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderIconsWithDropdowns; 